/* =========================================================================
 * THE AMERICAN CONFERENCE — the biggest single gap found in this project.
 *
 * ⚠️ I REPORTED THAT WE HELD ZERO RECORDS FOR IT. THAT WAS WRONG, AND THE
 * DEDUPE CHECK CAUGHT IT BEFORE ANYTHING WAS CREATED.
 *
 * The conference rebranded from "American Athletic Conference" to **American
 * Conference**, and our records already use the CURRENT name. My probe joined on
 * the old one, found nothing, and I read that as a missing conference — twenty
 * duplicate championships one command away.
 *
 * It is the silent-zero rule exactly: an empty result is a CLAIM ABOUT THE DATA
 * and is usually wrong. The only reason it cost nothing is that the probe
 * printed WHICH JOIN it tried, and `verify-gaps-vs-holdings.mjs` compares on
 * name tokens rather than trusting the key — so it matched "American Athletic
 * Conference Cross Country Men" against the held "American Conference Men's
 * Cross Country Championship" at 80% and withheld it.
 *
 * So the value here is NOT the records. It is the DATES.
 *
 * ⚠️ SOURCED FROM THE CONFERENCE'S OWN 2026-27 CHAMPIONSHIPS SCHEDULE, read in
 * a RENDERED browser. That distinction is the whole reason this file exists and
 * the previous nine-conference batch produced nothing: theamerican.org is a
 * JavaScript SPA, and a plain fetch returns an empty template shell rather than
 * a 404 — so research against it looks successful while silently falling back
 * to secondary sources.
 *
 * A CHAMPIONSHIP SCHEDULE is also a better source than a sponsored-sports page.
 * What a conference SCHEDULES A CHAMPIONSHIP FOR is a direct statement that the
 * championship exists, where a sport listing only says a sport is played — the
 * exact gap that makes Patriot League wrestling (EIWA) and Big West men's water
 * polo (MPSF) look like conference titles when they are not.
 *
 * ⚠️ AND IT CARRIES DATES. Every record below has a real published event date,
 * which is worth more than the record itself: ~2,580 records on this object sit
 * in NO DATE, invisible to Stages, Worklist and the Calendar — the three views
 * a rep uses to decide what to work this week.
 *
 * ⚠️ NO REGULAR-SEASON PAIRS, and that is a positive finding rather than an
 * omission. The American treats the postseason tournament winner as the SOLE
 * official conference title in nearly every sport — it is the whole-conference
 * inverse of the Big Ten. Pairing these would manufacture twenty-one
 * regular-season championships that the conference does not award. Encoded in
 * `conferenceTitles.js` under CONFERENCE_EXCEPTIONS.
 *
 * ⚠️ WHAT IS ABSENT IS ABSENT BECAUSE THE SCHEDULE OMITS IT: no men's swimming
 * (women's only), no wrestling, gymnastics, rowing, field hockey, beach
 * volleyball or men's lacrosse. Adding any of them "because a member school has
 * a team" is the phantom rule this project keeps relearning.
 * ========================================================================= */

const SRC = 'https://theamerican.org/sports/2026/7/7/2627champs.aspx'

/**
 * `date` is the FIRST day of the championship as the schedule prints it. A
 * multi-day event gets its opening date rather than a midpoint, because that is
 * the day the countdown should key off — a rep needs to be ready before it
 * starts, not halfway through.
 */
const A = (sport, gender, date, note = '') => ({
  st: 'AAC', state: '', body: 'American Conference', sport, gender,
  divisions: ['Championship'],
  source: SRC,
  segment: 'Conference', level: 'Conference',
  season: '2027',
  activity: false,
  date,
  note,
})

export const GAPS_AMERICAN = [
  /* ⚠️ A SEPARATE INDIVIDUAL TENNIS CHAMPIONSHIP, held in OCTOBER — six months
   * before the team championships in April. It is its own event on the
   * schedule, so it is its own record: an individual champion is a real
   * customer, and folding it into the team title would lose them. Gender is
   * left blank because the schedule prints it as one combined event. */
  A('Tennis', '', '2026-10-22', 'Individual championship — a separate event from the April team championships.'),

  A('Cross Country', 'Men', '2026-10-31'),
  A('Cross Country', 'Women', '2026-10-31'),
  A('Soccer', 'Men', '2026-11-06', 'First round at campus sites; semifinals and final at the No. 1 seed.'),
  A('Soccer', 'Women', '2026-11-08'),
  A('Volleyball', 'Women', '2026-11-20'),
  /* The American stages a football CHAMPIONSHIP GAME — one champion, decided on
   * the field, with no separate regular-season title. */
  A('Football', 'Men', '2026-12-05', 'Championship game. Site to be determined.'),
  A('Swimming & Diving', 'Women', '2027-02-17', 'The American sponsors WOMEN\'S swimming & diving only — there is no men\'s championship on the schedule.'),
  A('Indoor Track & Field', 'Men', '2027-02-26'),
  A('Indoor Track & Field', 'Women', '2027-02-26'),
  A('Basketball', 'Women', '2027-03-09'),
  A('Basketball', 'Men', '2027-03-10'),
  A('Golf', 'Women', '2027-04-19'),
  A('Tennis', 'Women', '2027-04-22'),
  A('Tennis', 'Men', '2027-04-22'),
  A('Golf', 'Men', '2027-04-25'),
  A('Lacrosse', 'Women', '2027-05-06'),
  A('Softball', 'Women', '2027-05-12'),
  A('Outdoor Track & Field', 'Men', '2027-05-13'),
  A('Outdoor Track & Field', 'Women', '2027-05-13'),
  A('Baseball', 'Men', '2027-05-26'),
]
